import React, { useEffect, useMemo } from "react";
import {
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { Checkbox, TextField } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import transactionPolicies from "validations/transactionPolicies";
import { formatCashFull, getCurrencySymbol } from "utils/formatCash";
import {
  CategorySetting,
  PendingCategorySetting,
  TransactionPolicy,
} from "hooks/settingPolicy.types";

interface CategorySettingRowProps {
  policy: TransactionPolicy | null;
  item: CategorySetting;
  /** Every unsaved edit on this policy, so a row can resync to its own. */
  updatingCategories: PendingCategorySetting[];
  onUpdateCategory: (
    item: CategorySetting,
    updated: { amount: string; isCategoryEnabled: boolean },
  ) => void;
}

const CategorySettingRow: React.FC<CategorySettingRowProps> = ({
  policy,
  item,
  updatingCategories,
  onUpdateCategory,
}) => {
  const { t } = useTranslation();

  const formik = useFormik({
    initialValues: {
      transactionsAmount: `${item.amount}`,
      // Formik only keeps a checkbox boolean when the current value already is
      // one; the API sends null here, which makes it build an array instead,
      // and [] is truthy, so the box could never be cleared again.
      isCategoryEnabled: Boolean(item.isCategoryEnabled),
    },
    validationSchema: transactionPolicies,
    onSubmit: (values) => {
      onUpdateCategory(item, {
        amount: values.transactionsAmount,
        isCategoryEnabled: values.isCategoryEnabled,
      });
    },
  });

  // Resync when the pending set changes: cleared means go back to the stored
  // values, and an entry for this row means adopt it.
  useEffect(() => {
    if (updatingCategories.length === 0) {
      formik.setFieldValue(
        "isCategoryEnabled",
        Boolean(item.isCategoryEnabled),
      );
      formik.setFieldValue("transactionsAmount", `${item.amount}`);
    }
    const pending = updatingCategories.find(
      (category) => category.categoryId === item.categoryId,
    );
    if (
      pending &&
      (pending.isCategoryEnabled !== formik.values.isCategoryEnabled ||
        `${pending.amount}` !== formik.values.transactionsAmount)
    ) {
      formik.setFieldValue(
        "isCategoryEnabled",
        Boolean(pending.isCategoryEnabled),
      );
      formik.setFieldValue("transactionsAmount", `${pending.amount}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatingCategories]);

  const isUpdatedAmount = useMemo(
    () => formik.values.transactionsAmount !== `${item.amount}`,
    [formik.values, item],
  );

  const isPolicyEnabled = Boolean(policy?.isPolicyTypeEnabled);

  const handleBlur = () => {
    if (
      formik.values.transactionsAmount !== `${item.amount}` &&
      !formik.errors.transactionsAmount
    ) {
      formik.handleSubmit();
    } else {
      formik.setFieldValue("transactionsAmount", `${item.amount}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    }
  };

  // Reached from the row and from the box. The box stops its own click from
  // bubbling, or the row would toggle a second time and cancel it out.
  //
  // The next value is computed and submitted directly rather than going through
  // handleSubmit after setFieldValue: setFieldValue is async, so handleSubmit
  // reads whatever formik has flushed by then. It happens to read the new value
  // today, but that is formik internals and React timing, not a contract.
  const toggleEnabled = async () => {
    const isCategoryEnabled = !formik.values.isCategoryEnabled;
    await formik.setFieldValue("isCategoryEnabled", isCategoryEnabled);
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) return;
    onUpdateCategory(item, {
      amount: formik.values.transactionsAmount,
      isCategoryEnabled,
    });
  };

  return (
    <ListItem disablePadding>
      <ListItemButton onClick={toggleEnabled} disabled={!isPolicyEnabled}>
        <Checkbox
          name="isCategoryEnabled"
          aria-label={item.categoryName}
          checked={Boolean(formik.values.isCategoryEnabled)}
          onChange={toggleEnabled}
          onClick={(e) => e.stopPropagation()}
          disabled={!isPolicyEnabled}
        />
        <ListItemText
          primary={`${item.categoryName}: ${formatCashFull(item.amount, item.currency)} ${isUpdatedAmount ? `${t("settingPolicies.updateTo")}` : ""}`}
        />
        <TextField
          name="transactionsAmount"
          size="md"
          leading={
            <Typography component="span" variant="inherit">
              {getCurrencySymbol(item.currency ?? "USD")}
            </Typography>
          }
          onChange={formik.handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
          value={formik.values.transactionsAmount}
          error={
            (formik.errors.transactionsAmount &&
              formik.touched.transactionsAmount) as boolean
          }
          disabled={!formik.values.isCategoryEnabled || !isPolicyEnabled}
        />
      </ListItemButton>
    </ListItem>
  );
};

export default CategorySettingRow;
